import { useCallback, useMemo, useState } from 'react';
import { useCustom, useCustomMutation } from '@refinedev/core';
import { Trash2 } from 'lucide-react';
import { notify } from '@/lib/notify';
import type {
  AdminBookBookmarkRowDto,
  AdminBookPaginatedDto,
  BookBookmarkLocator,
} from '@masters-club/types';
import { DataTable, type BulkAction, type Column } from '@/components/tables';
import { formatDate } from '@/components/universal';
import { useConfirm } from '@/hooks/use-confirm';

const PAGE_SIZE = 20;

interface BookBookmarksTabProps {
  bookId: number;
}

function displayName(
  row: Pick<AdminBookBookmarkRowDto, 'firstName' | 'lastName' | 'username' | 'telegramId' | 'userId'>,
): string {
  const parts = [row.firstName, row.lastName].filter(Boolean);
  if (parts.length > 0) {
    return parts.join(' ');
  }
  if (row.username) {
    return `@${row.username}`;
  }
  return `ID: ${row.telegramId ?? row.userId}`;
}

function formatLocator(locator: BookBookmarkLocator): string {
  if (!locator) { return '—'; }
  if (locator.type === 'page') {
    return `Стр. ${locator.value}`;
  }
  if (locator.type === 'seconds') {
    const m = Math.floor(locator.value / 60);
    const s = Math.floor(locator.value % 60);
    return `Гл. ${locator.chapterId}, ${m}:${String(s).padStart(2, '0')}`;
  }
  return `CFI: ${locator.value.slice(0, 40)}${locator.value.length > 40 ? '…' : ''}`;
}

export function BookBookmarksTab({ bookId }: BookBookmarksTabProps) {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const { confirm, ConfirmationDialog } = useConfirm();
  const { mutate: mutateDelete } = useCustomMutation();

  const { query } = useCustom<AdminBookPaginatedDto<AdminBookBookmarkRowDto>>({
    url: `/admin/books/${bookId}/bookmarks`,
    method: 'get',
    config: {
      query: {
        search: search || undefined,
        page,
        limit: PAGE_SIZE,
      },
    },
  });

  const refetch = query?.refetch;
  const payload = query?.data?.data;
  const items: AdminBookBookmarkRowDto[] = payload?.data ?? [];
  const total = payload?.meta?.total ?? items.length;
  const isLoading = query?.isLoading ?? false;

  const handleDelete = useCallback(async (selectedRows: AdminBookBookmarkRowDto[]) => {
    if (selectedRows.length === 0) { return; }
    const isSingle = selectedRows.length === 1;
    const ok = await confirm({
      title: 'Удалить закладку?',
      description: isSingle
        ? `Закладка пользователя ${displayName(selectedRows[0])} будет удалена без возможности восстановления.`
        : `${selectedRows.length} закладок будут удалены без возможности восстановления.`,
      confirmText: 'Удалить',
      cancelText: 'Отмена',
      variant: 'destructive',
    });
    if (!ok) { return; }

    let completed = 0;
    for (const row of selectedRows) {
      mutateDelete(
        {
          url: `/admin/books/${bookId}/bookmarks/${row.id}`,
          method: 'delete',
          values: {},
        },
        {
          onSuccess: () => {
            completed += 1;
            if (completed === selectedRows.length) {
              notify.success(isSingle ? 'Закладка удалена' : 'Закладки удалены');
              void refetch?.();
            }
          },
          onError: (error) => {
            notify.error(error instanceof Error ? error.message : 'Не удалось удалить закладку');
          },
        },
      );
    }
  }, [bookId, confirm, mutateDelete, refetch]);

  const columns = useMemo<Column<AdminBookBookmarkRowDto>[]>(
    () => [
      {
        key: 'userId',
        header: 'Пользователь',
        render: (_, row) => (
          <div>
            <p className="font-medium text-sm">{displayName(row)}</p>
            <p className="text-xs text-muted-foreground">
              {row.username ? `@${row.username}` : `ID: ${row.telegramId ?? row.userId}`}
            </p>
          </div>
        ),
      },
      {
        key: 'locator',
        header: 'Позиция',
        width: '180px',
        render: (_, row) => (
          <span className="text-xs tabular-nums text-muted-foreground">
            {formatLocator(row.locator)}
          </span>
        ),
      },
      {
        key: 'note',
        header: 'Заметка',
        render: (_, row) => (
          row.note ? (
            <p className="max-w-md text-sm line-clamp-2 whitespace-pre-wrap">{row.note}</p>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          )
        ),
      },
      {
        key: 'createdAt',
        header: 'Дата',
        width: '150px',
        render: (_, row) => (
          <span className="text-xs text-muted-foreground">{formatDate(row.createdAt)}</span>
        ),
      },
    ],
    [],
  );

  const bulkActions = useMemo<BulkAction<AdminBookBookmarkRowDto>[]>(
    () => [
      {
        label: 'Удалить',
        icon: <Trash2 className="h-4 w-4" />,
        variant: 'destructive',
        showOnlyWhenSelected: true,
        onClick: (selectedRows) => {
          void handleDelete(selectedRows);
        },
      },
    ],
    [handleDelete],
  );

  return (
    <div className="space-y-4">
      <DataTable
        columns={columns}
        data={items}
        total={total}
        page={page}
        pageSize={PAGE_SIZE}
        isLoading={isLoading}
        onPageChange={setPage}
        onSearch={(value) => {
          setSearch(value);
          setPage(1);
        }}
        searchValue={search}
        searchPlaceholder="Поиск по пользователю или заметке..."
        emptyMessage={search ? 'Ничего не найдено' : 'Нет закладок'}
        selectable
        bulkActions={bulkActions}
      />

      {ConfirmationDialog}
    </div>
  );
}
